import { AlertCircle, Check, X } from "lucide-react";
import { defaultStyles, FileIcon } from "react-file-icon";

import { Button } from "@needl-ai/common/components/ui/button";

import type { AttachmentStatus, FileItem } from "@/contexts/FileUploadContext";

import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { useFetchFeed } from "@/hooks/useFetchFeed";
import { useFetchNeedlDrive } from "@/hooks/useFetchNeedlDrive";

export const AttachmentList = ({ className = "" }: AttachmentListProps) => {
  const { attachments, removeAttachment } = useAskNeedlUpload();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      className={`space-y-3 p-4 border-t border-gray-100 dark:border-neutral-800 bg-gray-50/30 dark:bg-neutral-900/30 ${className}`}
    >
      <div className="flex gap-3 overflow-x-auto pb-2 pt-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
        {attachments.map((attachment) => (
          <AttachmentCard
            key={attachment.id}
            attachment={attachment}
            onRemove={removeAttachment}
          />
        ))}
      </div>
    </div>
  );
};

export const AttachmentCard = ({
  attachment,
  onRemove,
}: AttachmentCardProps) => {
  const { feedId } = useAskNeedlUpload();
  const { data: feedData, isLoading: isFeedLoading } = useFetchFeed(
    feedId || ""
  );

  const documents = feedData?.documents || [];
  const currentDocument = documents.find((doc) => doc.id === attachment.id);

  const { refetch, isLoading } = useFetchNeedlDrive(
    {
      access_key: currentDocument?.access_key || "",
      deeplink: currentDocument?.deeplink || "",
    },
    false
  );

  const fileExtension = getFileExtension(attachment.name, attachment.mime_type);

  // Only show remove button when NOT uploading, uploaded, or processing AND onRemove function is provided
  const showRemoveButton =
    !["UPLOADED", "UPLOADING", "PROCESSING"].includes(attachment.status) &&
    onRemove &&
    onRemove.toString() !== "() => {}";

  // Only allow clicking when status is COMPLETED
  const isClickable = attachment.status === "COMPLETED";

  const handleAttachmentClick = async () => {
    if (!isClickable) return;

    if (!currentDocument?.access_key || !currentDocument?.deeplink) {
      return;
    }

    try {
      const response = await refetch();

      if (response.data?.results && response.data.results.length > 0) {
        const firstResult = response.data.results[0];
        const objectLocation = firstResult?.object_location;
        if (objectLocation) {
          window.open(objectLocation, "_blank");
        }
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const getStatusText = () => {
    if (
      attachment.status === "UPLOADING" &&
      attachment.progress !== undefined
    ) {
      return `Uploading... ${Math.round(attachment.progress)}%`;
    }
    if (
      attachment.status === "UPLOADED" ||
      attachment.status === "PROCESSING"
    ) {
      return "Processing...";
    }
    if (attachment.status === "COMPLETED") {
      return (
        <span className="flex items-center gap-1">
          <span className="flex items-center justify-center w-3 h-3 rounded-full border-[1px] border-primary">
            <Check className="w-2 h-2 text-primary" strokeWidth={2} />
          </span>
          Ready
        </span>
      );
    }
    if (
      attachment.status === "PROCESSING_ERROR" ||
      attachment.status === "UPLOADING_ERROR"
    ) {
      return "Error";
    }
    return "";
  };

  return (
    <div
      className={`relative flex items-center gap-2.5 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl py-2.5 px-3.5 hover:border-gray-300 dark:hover:border-neutral-600 hover:shadow-md dark:hover:shadow-neutral-900/30 transition-all duration-200 group min-w-[200px] flex-shrink-0 ${
        isLoading || isFeedLoading
          ? "cursor-not-allowed"
          : isClickable
            ? "cursor-pointer"
            : "cursor-default"
      }`}
      onClick={handleAttachmentClick}
    >
      {showRemoveButton && (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(attachment.id);
          }}
          className="absolute p-1 -top-2 -right-2 bg-white dark:bg-neutral-800 hover:bg-red-50 dark:hover:bg-red-900/20 h-6 w-6 rounded-full border border-gray-200 dark:border-neutral-700 hover:border-red-200 dark:hover:border-red-700 shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-200"
        >
          <X className="h-3.5 w-3.5 text-gray-500 dark:text-neutral-400 hover:text-red-500 dark:hover:text-red-400" />
        </Button>
      )}

      <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
        <StatusIconDisplay
          status={attachment.status}
          progress={attachment.progress}
          fileExtension={fileExtension}
        />
      </div>

      <div className="flex flex-col flex-1 min-w-0 items-start">
        <span
          className="text-sm font-semibold text-gray-700 dark:text-neutral-300 truncate leading-tight w-full text-left"
          title={attachment.name}
        >
          {getTrimmedFileName(attachment.name, 20)}
        </span>
        <span className="text-xs text-primary dark:text-primary/90 font-medium mt-1.5 text-left">
          {getStatusText()}
        </span>
      </div>
    </div>
  );
};

// Helper function to get file extension from filename or MIME type
const getFileExtension = (fileName: string, mimeType?: string): string => {
  // First try to get extension from filename
  const extensionFromName = fileName.split(".").pop()?.toLowerCase();
  if (extensionFromName && extensionFromName !== fileName.toLowerCase()) {
    return extensionFromName;
  }

  // Fallback to MIME type mapping
  const mimeToExtension: Record<string, string> = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
    "text/plain": "txt",
    "text/csv": "csv",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "application/zip": "zip",
    "application/x-zip-compressed": "zip",
    "application/json": "json",
    "text/html": "html",
    "text/css": "css",
    "text/javascript": "js",
    "application/javascript": "js",
  };

  return mimeType ? mimeToExtension[mimeType] || "file" : "file";
};

// Helper function to trim long file names
const getTrimmedFileName = (
  fileName: string,
  maxLength: number = 25
): string => {
  if (fileName.length <= maxLength) {
    return fileName;
  }

  const extension = fileName.split(".").pop();
  const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf("."));

  if (
    extension &&
    nameWithoutExtension.length > maxLength - extension.length - 4
  ) {
    const trimmedName = nameWithoutExtension.substring(
      0,
      maxLength - extension.length - 4
    );
    return `${trimmedName}...${extension}`;
  }

  return `${fileName.substring(0, maxLength - 3)}...`;
};

const StatusIconDisplay = ({
  status,
  progress = 0,
  fileExtension,
}: {
  status: AttachmentStatus;
  progress?: number;
  fileExtension: string;
}) => {
  if (status === "UPLOADING") {
    return (
      <div
        className="relative w-10 h-10 flex items-center justify-center"
        role="status"
        aria-label={
          progress !== undefined
            ? `File uploading ${Math.round(progress)}%`
            : "File uploading"
        }
      >
        <svg
          className="absolute w-10 h-10"
          viewBox="0 0 40 40"
          style={{ transform: "rotate(-90deg)" }}
        >
          <circle
            cx="20"
            cy="20"
            r="17"
            fill="none"
            stroke="currentColor"
            className="text-gray-200 dark:text-neutral-600"
            strokeWidth="2.5"
          />
          <circle
            cx="20"
            cy="20"
            r="17"
            fill="none"
            stroke="currentColor"
            className="text-primary dark:text-primary"
            strokeWidth="2.5"
            strokeDasharray={`${2 * Math.PI * 17}`}
            strokeDashoffset={`${2 * Math.PI * 17 * (1 - progress / 100)}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center">
            <FileIcon
              extension={fileExtension}
              {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
            />
          </div>
        </div>
      </div>
    );
  }

  if (["PROCESSING", "UPLOADED"].includes(status)) {
    return (
      <div
        className="relative w-10 h-10 flex items-center justify-center"
        role="status"
        aria-label={
          status === "UPLOADED" ? "Upload complete" : "File processing"
        }
      >
        <svg className="absolute w-10 h-10" viewBox="0 0 40 40">
          <circle
            cx="20"
            cy="20"
            r="17"
            fill="none"
            stroke="currentColor"
            className="text-gray-200 dark:text-neutral-600"
            strokeWidth="2.5"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-[2.5px] border-transparent border-t-primary dark:border-t-primary"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-4 h-4 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center">
            <FileIcon
              extension={fileExtension}
              {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
            />
          </div>
        </div>
      </div>
    );
  }

  if (["PROCESSING_ERROR", "UPLOADING_ERROR"].includes(status)) {
    return (
      <div
        className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 flex items-center justify-center"
        role="alert"
        aria-label="Upload error"
      >
        <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
      </div>
    );
  }
  return (
    <div
      className="w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center"
      aria-label="Upload complete"
    >
      <div className="w-4 h-4 rounded-full flex items-center justify-center">
        <FileIcon
          extension={fileExtension}
          {...defaultStyles[fileExtension as keyof typeof defaultStyles]}
        />
      </div>
    </div>
  );
};

type AttachmentCardProps = {
  attachment: FileItem;
  onRemove?: (id: string) => void;
};

type AttachmentListProps = {
  className?: string;
};
